<template>
  <div class="kd-close-statistics-page">
    <div class="filter-container">
      <el-form :inline="true" :model="queryParams" label-width="75px">
        <el-form-item label="日期范围">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="-"
            value-format="yyyy-MM-dd"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
          />
        </el-form-item>
        <el-form-item label="国家">
          <el-select
            v-model="queryParams.countryIdList"
            multiple
            collapse-tags
            filterable
            clearable
            placeholder="全部国家"
          >
            <el-option
              v-for="item in nationList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="责任部门">
          <el-select
            v-model="queryParams.responsibilityDeptList"
            multiple
            collapse-tags
            filterable
            clearable
            placeholder="全部责任部门"
          >
            <el-option
              v-for="item in deptList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
        <el-button type="primary" plain size="small" @click="handleReset">重置</el-button>
      </el-form>
    </div>

    <div class="toolbar">
      <div class="toolbar-left">
        <div class="page-title">KD质量反馈问题数量统计</div>
        <div class="page-sub-title">统计新增问题数、关闭问题数与关闭率（关闭定义：进度达到5/6或6/6）</div>
      </div>
      <div class="change-btn">
        <div
          :class="['btn btn-first', { active: queryParams.dimension === 'time' }]"
          @click="handleDimensionChange('time')"
        >按月</div>
        <div
          :class="['btn', { active: queryParams.dimension === 'country' }]"
          @click="handleDimensionChange('country')"
        >按国家</div>
        <div
          :class="['btn btn-last', { active: queryParams.dimension === 'responsibilityDept' }]"
          @click="handleDimensionChange('responsibilityDept')"
        >按责任部门</div>
      </div>
    </div>

    <div class="chart-card">
      <div class="card-title">{{ chartTitle }}</div>
      <chart ref="chartRef" :height="420" :option="chartOption()" />
    </div>

    <div class="table-card">
      <div class="card-title">统计明细</div>
      <el-table
        :data="tableData"
        border
        stripe
        highlight-current-row
        empty-text="暂无统计数据"
        show-summary
        :summary-method="getSummaries"
      >
        <el-table-column
          :label="dimensionLabel"
          prop="label"
          min-width="140"
          show-overflow-tooltip
        />
        <el-table-column prop="newCount" label="新增问题数" min-width="110" align="center" />
        <el-table-column prop="closeCount" label="关闭问题数" min-width="110" align="center" />
        <el-table-column prop="closeRate" label="关闭率" min-width="90" align="center">
          <template #default="{ row }">{{ row.closeRate }}%</template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import Chart from '@/components/chart'
import { closeStatApi, closeStatOptionsApi } from '@/api/feedbackKD'

const STORAGE_KEY = "searchParams"
const STORAGE_FIELD = "kdCloseStatistics"
export default {
  name: "KdCloseStatistics",
  components: { Chart },
  data() {
    return {
      dateRange: [],
      queryParams: {
        dimension: "time",
        startTime: null,
        endTime: null,
        countryIdList: [],
        responsibilityDeptList: []
      },
      nationList: [],
      deptList: [],
      statData: { xAxis: [], newCountList: [], closeCountList: [], closeRateList: [] }
    };
  },
  computed: {
    chartTitle() {
      const map = { time: "按月", country: "按国家", responsibilityDept: "按责任部门" };
      return `KD问题新增/关闭统计（${map[this.queryParams.dimension] || ""}）`;
    },
    dimensionLabel() {
      const map = { time: "月份", country: "国家", responsibilityDept: "责任部门" };
      return map[this.queryParams.dimension] || "";
    },
    tableData() {
      return (this.statData.xAxis || []).map((label, i) => ({
        label,
        newCount: this.statData.newCountList[i] || 0,
        closeCount: this.statData.closeCountList[i] || 0,
        closeRate: this.statData.closeRateList[i] || "0.0"
      }));
    }
  },
  created() {
    this.restoreSearchState();
    this.loadOptions();
    this.fetchData();
  },
  methods: {
    /**
     * 获取默认查询参数
     */
    getDefaultQueryParams() {
      return {
        dimension: "time",
        startTime: null,
        endTime: null,
        countryIdList: [],
        responsibilityDeptList: []
      };
    },

    /**
     * 从缓存恢复查询条件
     */
    restoreSearchState() {
      const rawSearchParams = sessionStorage.getItem(STORAGE_KEY);
      if (!rawSearchParams) {
        return;
      }
      const cacheData = JSON.parse(rawSearchParams)?.[STORAGE_FIELD];
      if (!cacheData) {
        return;
      }
      this.queryParams = {
        ...this.getDefaultQueryParams(),
        ...cacheData.queryParams
      };
      this.queryParams.countryIdList = [...(this.queryParams.countryIdList || [])];
      this.queryParams.responsibilityDeptList = [...(this.queryParams.responsibilityDeptList || [])];
      this.dateRange = Array.isArray(cacheData.dateRange) ? [...cacheData.dateRange] : [];
    },

    /**
     * 保存查询条件到缓存
     */
    persistSearchState() {
      const currentSearchParams = sessionStorage.getItem(STORAGE_KEY);
      const cacheData = currentSearchParams ? JSON.parse(currentSearchParams) : {};
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...cacheData,
          [STORAGE_FIELD]: {
            queryParams: {
              ...this.queryParams,
              countryIdList: [...(this.queryParams.countryIdList || [])],
              responsibilityDeptList: [...(this.queryParams.responsibilityDeptList || [])]
            },
            dateRange: [...(this.dateRange || [])]
          }
        })
      );
    },

    /**
     * 清空查询条件缓存
     */
    clearSearchState() {
      const currentSearchParams = sessionStorage.getItem(STORAGE_KEY);
      if (!currentSearchParams) {
        return;
      }
      const cacheData = JSON.parse(currentSearchParams);
      delete cacheData[STORAGE_FIELD];
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cacheData));
    },
    async loadOptions() {
      try {
        const res = await closeStatOptionsApi();
        const payload = res.data || {};
        this.nationList = (payload.countryList || []).map(n => ({ label: n.label, value: n.value }));
        this.deptList = (payload.responsibilityDeptList || []).map(d => ({ label: d.label, value: d.value }));
      } catch (e) {
        console.error(e);
      }
    },
    async fetchData() {
      try {
        const params = { ...this.queryParams };
        if (this.dateRange && this.dateRange.length === 2) {
          params.startTime = this.dateRange[0];
          params.endTime = this.dateRange[1];
        } else {
          params.startTime = null;
          params.endTime = null;
        }
        const res = await closeStatApi({ data: params });
        const payload = res.data || {};
        this.statData = {
          xAxis: payload.xAxis || payload.xaxis || [],
          newCountList: payload.newCountList || payload.newcountlist || [],
          closeCountList: payload.closeCountList || payload.closecountlist || [],
          closeRateList: payload.closeRateList || payload.closeratelist || []
        };
      } catch (e) {
        console.error(e);
      }
    },
    handleSearch() {
      this.persistSearchState();
      this.fetchData();
    },
    handleReset() {
      this.dateRange = [];
      this.queryParams = this.getDefaultQueryParams();
      this.clearSearchState();
      this.fetchData();
    },
    handleDimensionChange(dim) {
      this.queryParams.dimension = dim;
      this.persistSearchState();
      this.fetchData();
    },
    chartOption() {
      const xAxis = this.statData.xAxis || [];
      const newList = this.statData.newCountList || [];
      const closeList = this.statData.closeCountList || [];
      const rateList = (this.statData.closeRateList || []).map(v => parseFloat(v));
      return {
        tooltip: { trigger: "axis", axisPointer: { type: "cross" } },
        legend: { data: ["新增问题数", "关闭问题数", "关闭率"] },
        xAxis: [{ type: "category", data: xAxis, axisLabel: { rotate: 30 } }],
        yAxis: [
          { type: "value", name: "问题数", minInterval: 1 },
          { type: "value", name: "关闭率(%)", min: 0, max: 100, splitLine: { show: false }, axisLabel: { formatter: "{value}%" } }
        ],
        series: [
          { name: "新增问题数", type: "bar", data: newList, itemStyle: { color: "#5B8FF9" } },
          { name: "关闭问题数", type: "bar", data: closeList, itemStyle: { color: "#61DDAA" } },
          { name: "关闭率", type: "line", yAxisIndex: 1, data: rateList,
            itemStyle: { color: "#F6BD16" }, label: { show: true, formatter: p => p.value + "%" } }
        ]
      };
    },
    getSummaries({ columns, data }) {
      return columns.map((col, i) => {
        if (i === 0) return "合计";
        if (col.property === "closeRate") {
          const totalNew = data.reduce((s, r) => s + Number(r.newCount), 0);
          const totalClose = data.reduce((s, r) => s + Number(r.closeCount), 0);
          return totalNew === 0 ? "0.0%" : (totalClose * 100 / totalNew).toFixed(1) + "%";
        }
        const sum = data.reduce((s, r) => s + Number(r[col.property] || 0), 0);
        return sum;
      });
    }
  }
};
</script>
<style src="@/assets/css/qualityStyle.css"></style>
<style scoped>
.kd-close-statistics-page {
  /* padding: 16px;
  background: #f5f7fa;
  min-height: calc(100vh - 84px); */
}
.filter-container {
  padding: 16px 0;
  margin-bottom: 12px;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.page-title {
  font-size: 18px;
  font-weight: bold;
  color: #000;
}
.page-sub-title {
  font-size: 13px;
  color: #999;
  margin-top: 6px;
}
.change-btn {
  display: flex;
  overflow: hidden;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  cursor: pointer;
}
.change-btn .btn {
  min-width: 80px;
  padding: 8px 18px;
  background: #fff;
  color: #666;
  text-align: center;
  transition: all 0.2s ease;
}
.change-btn .btn.active {
  background: var(--theme-color);
  color: #fff;
}
.chart-card,
.table-card {
  padding: 15px;
  border-radius: 0px;
  background-color: var(--dash-color);
  margin-bottom: 12px;
}
.card-title {
  margin-bottom: 12px;
  padding-left: 10px;
  border-left: 4px solid var(--theme-color);
  font-size: 16px;
}

</style>



